/**
 * The weekly score (docs/SPEC.md section 2). Up to 1,000 points. For every whole minute since the hatch, the pet earns
 * points in proportion to its average meter level at the start of that minute, scaled so that a pet kept at 100% for
 * every minute of the week's own length (10,020, 10,080 or 10,140 minutes) earns exactly 1,000. The score never goes
 * down. A pet that dies keeps what it earned and is marked lost. A late hatch has fewer minutes, so it cannot reach
 * 1,000. A week in which the app was never opened has no log and scores 0 with no entry.
 */
import { preparePet, stateAt } from "./pet";
import { METER_IDS, type PetLog } from "./types";
import { weekBounds } from "./week";

export const MAX_SCORE = 1000;
const MINUTE = 60_000;

export interface ScoreResult {
  points: number;
  /** False when the app was never opened that week: there is no picture book entry. */
  entry: boolean;
  /** True when the pet died during the week; it keeps its points but is not added to the picture book. */
  lost: boolean;
  /** The whole minutes that earned points so far. */
  minutes: number;
}

export function scoreAt(log: PetLog | null, now: number): ScoreResult {
  if (!log) return { points: 0, entry: false, lost: false, minutes: 0 };

  const prepared = preparePet(log);
  const { end, minutes: weekMinutes } = weekBounds(log.weekId, log.timeZone);
  const stop = Math.min(now, prepared.death ?? Infinity, end);
  const minutes = stop > log.hatchedAt ? Math.floor((stop - log.hatchedAt) / MINUTE) : 0;

  let percentMinutes = 0;
  for (let k = 0; k < minutes; k++) {
    const state = stateAt(prepared, log.hatchedAt + k * MINUTE);
    let sum = 0;
    for (const meter of METER_IDS) sum += state.meters[meter].value;
    percentMinutes += sum / METER_IDS.length;
  }

  const points = Math.min(MAX_SCORE, (percentMinutes * MAX_SCORE) / (weekMinutes * 100));
  const lost = prepared.death !== null && prepared.death <= end && now >= prepared.death;
  return { points, entry: true, lost, minutes };
}
